import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import Text from 'tio-reimagined/components/text';
import type { ComponentLike } from '@glint/template';

interface BoostActionLinkSignature {
  Args: {
    /**
     * Ember route to navigate to
     */
    route: string;
    /**
     * Icon component to display
     */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /**
     * Action description text
     */
    label: string;
    /**
     * Points value to display (e.g., "+15 pts")
     */
    points: string;
    /**
     * Icon color class
     * @default "text-gray-600"
     */
    iconClass?: string;
  };
  Element: HTMLAnchorElement;
}

/**
 * BoostActionLink - Score boost action item with icon, label, and points
 *
 * Displays a clickable row with an icon, action description, and points value.
 * Used in score boost sections to encourage user engagement.
 *
 * @example Basic usage
 * ```gts
 * <BoostActionLink
 *   @route="authenticated.reimagined.skills"
 *   @icon={{PhLightning}}
 *   @label="Complete a skill assessment"
 *   @points="+15 pts"
 *   @iconClass="tio-fg-info"
 * />
 * ```
 *
 * @example In a list
 * ```gts
 * <div class="space-y-2">
 *   <BoostActionLink
 *     @route="authenticated.reimagined.skills"
 *     @icon={{PhLightning}}
 *     @label="Complete a skill assessment"
 *     @points="+15 pts"
 *     @iconClass="tio-fg-info"
 *   />
 *   <BoostActionLink
 *     @route="authenticated.reimagined.recommendations"
 *     @icon={{PhGraduationCap}}
 *     @label="Start a recommended course"
 *     @points="+25 pts"
 *     @iconClass="tio-fg-brand"
 *   />
 * </div>
 * ```
 */
const BoostActionLink: TOC<BoostActionLinkSignature> = <template>
  <LinkTo
    @route={{@route}}
    class="tio-row-between p-3 tio-bg-surface rounded-lg hover:shadow-sm transition-shadow"
    ...attributes
  >
    <div class="tio-row-tight">
      <@icon @size={{18}} class={{if @iconClass @iconClass "text-gray-600"}} />
      <Text @size="sm">{{@label}}</Text>
    </div>
    <Text @size="sm" @weight="medium" class="tio-fg-success">{{@points}}</Text>
  </LinkTo>
</template>;

export default BoostActionLink;
